import { DEFAULT_BRAND } from '../brand/BrandKitContext.tsx'
import { HEX_COLOUR } from '../brand/palette.ts'

/**
 * Escapes text for use in HTML markup (as opposed to `renderMarkdown`, which
 * sanitizes a whole markdown document). Needed here because `brand.name` is
 * free text interpolated directly into HTML — see `buildDocHtml`.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Build a Word-compatible .doc HTML document, branded with the service's own
 * kit (name + primary colour — never a hard-coded brand). The draft notice,
 * when content is unapproved, already lives inside `bodyHtml`: the server
 * bakes it into the markdown on every render/bundle path (`withDraftNotice`
 * in `server/draft.ts`), so it survives copy-to-clipboard as well as
 * download. This function must not add a second copy of its own.
 *
 * `brand` comes from Settings — free text, stored unvalidated in
 * localStorage — so it must be treated as untrusted before it reaches this
 * generated HTML. `primary` is checked against the same hex shape the
 * Settings colour picker uses, falling back to the default brand colour
 * rather than emitting whatever was typed. `name` is HTML-escaped rather
 * than interpolated raw.
 */
export function buildDocHtml(brand: { name: string; primary: string }, bodyHtml: string): string {
  const primary = HEX_COLOUR.test(brand.primary) ? brand.primary : DEFAULT_BRAND.primary
  const name = escapeHtml(brand.name)
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8">
      <title>${name}</title>
      <style>
        body { font-family: Calibri, Arial, sans-serif; color: #2a2a24; }
        header { border-bottom: 3px solid ${primary}; padding-bottom: 8px; margin-bottom: 20px; }
        header strong { font-size: 1.1em; color: ${primary}; }
        h1, h2, h3 { color: ${primary}; }
      </style>
    </head>
    <body><header><strong>${name}</strong></header>${bodyHtml}</body></html>`
}

/**
 * Downloads a single document as a .doc. Print blanks are resolved here (not
 * left to the caller) so every export path — this one and the bundle zip —
 * renders `[[Label]]` as a fillable rule rather than a literal bracket.
 */
export function downloadDoc(filename: string, brand: { name: string; primary: string }, bodyHtml: string): void {
  const blob = new Blob([buildDocHtml(brand, renderPrintBlanks(bodyHtml))], { type: 'application/msword' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.doc') ? filename : `${filename}.doc`
  a.click()
  URL.revokeObjectURL(url)
}

export async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text)
}

/**
 * Print blanks are for a person to complete on the printed or on-screen form.
 * They must never look like a templating bug, so they render as a labelled
 * rule — on screen (RenderedDoc, DocumentView) and in the exported .doc alike.
 *
 * Colours use `var(--token, #666)`: on screen this resolves to the app's
 * theme-aware tokens (readable in both light and dark), while an exported
 * .doc is a standalone HTML file with no access to index.css, so the `#666`
 * fallback is what Word actually renders.
 */
export function renderPrintBlanks(html: string): string {
  return html.replace(/\[\[([^\]]+)\]\]/g, (_m, label: string) =>
    `<span style="display:inline-block;min-width:12em;border-bottom:1px solid var(--line, #666);">&nbsp;</span>` +
    `<span style="font-size:0.8em;color:var(--muted, #666);"> ${label}</span>`)
}
